import React from 'react';
import { useCustomerAccountsQuery } from 'api/cms';
import _ from 'lodash';
import { BsChevronDown as ChevronDownIcon } from 'react-icons/bs';
import Dropdown from 'react-bootstrap/Dropdown';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import { Link, useLocation } from 'react-router-dom';
import qs from 'qs';
import './index.scss';
import { translations } from '@janus.team/janus-particles';
import { ReactComponent as RIcon } from 'icons/Rackspace_Icon_Red.svg';
import { ReactComponent as KubernetesIcon } from 'icons/kubernetes.svg';
import { ReactComponent as AwsIcon } from 'icons/aws.svg';
import { ReactComponent as VMWareIcon } from 'icons/vmware_logo.svg';
import { ReactComponent as GcpIcon } from 'icons/google-cloud-seeklogo.com.svg';
import { ReactComponent as AzureIcon } from 'icons/azure.svg';
import * as config from 'config';

export const useCurrentAccount = () => {
  const parsedQuery = qs.parse(useLocation()?.search || '', { ignoreQueryPrefix: true }) || {};
  const { accountId, accountType } = parsedQuery || {};
  const accountsQuery = useCustomerAccountsQuery();
  const account = _.find(accountsQuery?.data, { id: accountId, type: accountType });
  return account || _.head(accountsQuery?.data);
};

export const useAccountTypeLabel = (account) => {
  const t = translations.useT();
  const ACCOUNT_TYPE_TO_DESCRIPTOR_MAP = {
    AZURE: t('Azure'),
    CLOUD: t('OpenStack Public Cloud'),
    FANATICAL_SUPPORT_FOR_AWS: t('AWS'),
    MANAGED_GCP: t('GCP'),
    MANAGED_HOSTING: t('Managed Hosting'),
    MANAGED_VMC: t('VMware Cloud on AWS'),
    RPC_V: t('VMware Private Cloud'),
    MPK: t('Managed Platform for Kubernetes')
  };
  return _.get(ACCOUNT_TYPE_TO_DESCRIPTOR_MAP, _.toUpper(_.snakeCase(account?.type))) || account?.type;
};

export const useAccountTypeIcon = (account) => {
  const ACCOUNT_TYPE_TO_DESCRIPTOR_MAP = {
    AZURE: AzureIcon,
    CLOUD: RIcon,
    FANATICAL_SUPPORT_FOR_AWS: AwsIcon,
    MANAGED_GCP: GcpIcon,
    MANAGED_HOSTING: RIcon,
    MANAGED_VMC: VMWareIcon,
    RPC_V: VMWareIcon,
    MPK: KubernetesIcon
  };
  return _.get(ACCOUNT_TYPE_TO_DESCRIPTOR_MAP, _.toUpper(_.snakeCase(account?.type))) || RIcon;
};

const IconOf = ({ account }) => {
  const label = useAccountTypeLabel(account);
  const IconComponent = useAccountTypeIcon(account);
  return (
    <IconComponent className="account-provider-icon" role="img" title={label} /> 
  );
};

const AccountSelectorDropdownToggle = React.forwardRef((props, ref) => {
  const account = useCurrentAccount();
  return (
    <div ref={ref} className="d-flex align-items-center gap-4 text-start account-selector-dropdown-toggle" aria-haspopup="menu">
      <AccountMediaObject account={account} />
      <ChevronDownIcon className="text-gray700 ms-auto" style={{minWidth: '1.5rem', width: '1.5rem'}}/>
    </div>
  );
});

const matchesAccountTextFilter = (filterValue) => (account) => _.includes(
  _.toLower(JSON.stringify(_.values(_.pick(account, ['name', 'id'])))),
  _.toLower(_.trim(filterValue || ''))
);

const AccountSelectorDropdownMenu = React.forwardRef(({ style, className, 'aria-labelledby': labeledBy }, ref) => {
  const t = translations.useT();
  const [inputValue, setInputValue] = React.useState('');
  const [accountSelectorTextFilterValue, setAccountSelectorTextFilterValue] = React.useState(inputValue);
  const { pathname } = useLocation();
  const search = qs.parse(useLocation()?.search || '', { ignoreQueryPrefix: true });
  const searchFieldRef = React.useRef(null);
  const baseAccounts = useCustomerAccountsQuery()?.data;
  const accounts = React.useMemo(() =>
    _.filter(baseAccounts, matchesAccountTextFilter(accountSelectorTextFilterValue)),
    [baseAccounts, accountSelectorTextFilterValue]
  );
  
  React.useEffect(() => {
    const timeoutId = global.setTimeout(() => _.invoke(searchFieldRef, 'current.focus'), 1);
    return () => global.clearTimeout(timeoutId);
  }, [searchFieldRef]);
  
  React.useEffect(() => {
    const timeoutId = global.setTimeout(() => setAccountSelectorTextFilterValue(inputValue), config.debounceDelay());
    return () => global.clearTimeout(timeoutId);
  }, [inputValue, setAccountSelectorTextFilterValue]);
  
  return (
    <div ref={ref} style={style} className={className} role="menu" aria-labelledby={labeledBy}>
      <div className="px-6 py-4">
        <Form.Control 
          ref={searchFieldRef}
          placeholder={t('Filter Accounts')}
          id="account-selector-text-filter"
          className="account-selector-text-filter w-100"
          name="accountSelectorTextFilter"
          value={inputValue}
          onChange={({ target: { value }}) => setInputValue(value)}
        />
      </div>
      <div className="list-group-flush" style={{ maxHeight: '25vh', overflowY: 'scroll' }}>
        {_.map(accounts, (account) => {
          const key = `${account?.type}--${account?.id}`;
          const toSearch = qs.stringify(_.merge({}, search, { accountId: account?.id, accountType: account?.type }));
          return (
            <Dropdown.Item key={key} className="mt-4" as={Link} to={{ pathname, search: toSearch }}>
              <AccountMediaObject account={account} />
            </Dropdown.Item>
          );
        })}
      </div>
    </div>
  );
});

const AccountMediaObject = ({ account }) => {
  const name = account?.name;
  return (
    <div className="account-media-object d-flex gap-6 align-items-center text-truncate">
      <IconOf account={account} />
      <div className="text-truncate">
        <header className="text-truncate">
          {name}
        </header>
        <small className="d-block text-muted text-truncate">{account?.id}</small>
      </div>
    </div>
  );
};

export const AccountSelector = () => {
  const t = translations.useT();
  return (
    <div className="account-select container-xxl container-fluid mb-10">
      <h2 className="my-6">Account</h2>
      <Dropdown>
        <Dropdown.Toggle aria-label={t('Select Account')} as={Button} variant="outline border" className="overflow-hidden text-truncate account-selector-dropdown-trigger">
          <AccountSelectorDropdownToggle />
        </Dropdown.Toggle>
        <Dropdown.Menu as={AccountSelectorDropdownMenu} className="account-selector-dropdown-menu shadow" />
      </Dropdown>
    </div>
  );
};